import type { Notification } from '@/api/notificationsApi'
import { LIS_REVIEW_PARAM } from '@/lib/lisReview'

/**
 * Resolves a notification to the in-app route it should open.
 *
 * Route knowledge lives HERE, not in the backend: the backend stores structured
 * context (`payload` = ids) rather than a URL, so renaming a route (as /alerts →
 * /notifications was) can never leave stale links baked into old rows.
 *
 * Returns null when a notification isn't actionable — callers render those as
 * plain, non-clickable rows.
 */

interface NotificationContext {
  patientMrn?: string | null
  procedureId?: string | null
  din?: string | null
  resultId?: string | null
}

function contextOf(n: Notification): NotificationContext {
  return n.payload && typeof n.payload === 'object' && !Array.isArray(n.payload)
    ? (n.payload as NotificationContext)
    : {}
}

const payloadResultId = (n: Notification): string | null => contextOf(n).resultId ?? null

const patientProcedure = (mrn: string, procedureId?: string | null): string =>
  procedureId
    ? `/patients/${encodeURIComponent(mrn)}/procedures/${encodeURIComponent(procedureId)}`
    : `/patients/${encodeURIComponent(mrn)}`

export function notificationLink(n: Notification): string | null {
  const { patientMrn, procedureId } = contextOf(n)

  switch (n.category) {
    case 'PATIENT_INTAKE':
      // Fall back to subjectRef (the MRN) for rows created before the payload
      // carried routing context.
      return patientMrn
        ? patientProcedure(patientMrn, procedureId)
        : n.subjectRef
          ? patientProcedure(n.subjectRef)
          : null

    case 'LAB_RESULT':
      // subjectRef is the DIN here — not routable on its own, so this needs the
      // patient resolved in the payload.
      return patientMrn ? patientProcedure(patientMrn, procedureId) : null

    case 'PROCEDURE_DUE':
      return patientMrn ? patientProcedure(patientMrn, procedureId) : '/procedures'

    case 'FORM_NOTIFICATION_RULE':
      // Fired by a field's notification rule on form-submission completion
      // (see cgt-backend form-submissions.service.ts). No submission-detail
      // route exists today, so this routes to the patient/procedure the same
      // as the other patient-context categories — non-clickable if the
      // payload lacks a patientMrn rather than guessing.
      return patientMrn ? patientProcedure(patientMrn, procedureId) : null

    case 'LIS_RESULT_REVIEW': {
      // subjectRef is the LisResult id. The patient/procedure page opens the
      // review dialog for it from the query param, so the link needs the
      // procedure; a result with no run behind it cannot be routed.
      const resultId = typeof n.subjectRef === 'string' && n.subjectRef ? n.subjectRef : payloadResultId(n)
      if (!resultId || !patientMrn) return null
      return `${patientProcedure(patientMrn, procedureId)}?${LIS_REVIEW_PARAM}=${encodeURIComponent(resultId)}`
    }

    case 'SUPPORT_TICKET': {
      // payload.number is the Smart Support ticket number; subjectRef is its
      // key (TKT-04813), the fallback for a row without the number.
      const raw = (n.payload as { number?: unknown } | null | undefined)?.number
      const number =
        typeof raw === 'number' ? raw : typeof n.subjectRef === 'string' ? Number(/^TKT-0*(\d+)$/.exec(n.subjectRef)?.[1]) : NaN
      return Number.isInteger(number) && number > 0 ? `/support/${number}` : '/support'
    }

    default:
      return null
  }
}
